import React from 'react';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Loader from '../atoms/Loader';
import NamedBox from '../atoms/NamedBox';
import Ranks, { RanksType } from '../atoms/Ranks';
import Accordion from '../atoms/Accordion';
import { FormatType, getEventFormatType, getEventName } from '../services/event-names-and-formats';
import { inchesToFeetAndInches, secondsToMinutesSecondsMs } from '../services/converter';
import Player from '../types/Player';
import Team from '../types/Team';
import Position from '../types/Position';
import College from '../types/College';
import HighSchool from '../types/HighSchool';
import { formatDate } from '../services/player';
import { PlayerEventStatsComparison } from '../types/StatsPlayer';
import Column from '../types/Column';
import {
  DEFAULT_PROFILE_STATS_COLUMNS,
  getCollegeHeaderItemByColumn,
  getNflHeaderItemByColumn,
  getProfileStatsComparisonRankValue
} from '../services/profile-stats-categories';
import { State } from '../redux/reducers';

interface PlayerTrackAndFieldDataProps {
  loading: boolean;
  player?: Player;
  collegeTeam?: Team;
  nflTeam?: Team;
  position?: Position;
  sprints: Array<PlayerEventStatsComparison>;
  relays: Array<PlayerEventStatsComparison>;
  hurdles: Array<PlayerEventStatsComparison>;
  jumps: Array<PlayerEventStatsComparison>;
  throws: Array<PlayerEventStatsComparison>;
  columns?: Column[];
  isNFL: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  playerTrackAndFieldData: {
    borderBottom: 0,
    position: 'relative',
  },
}), { name: PlayerTrackAndFieldData.name });

export default function PlayerTrackAndFieldData (props: PlayerTrackAndFieldDataProps) {
  const {
    loading,
    sprints,
    relays,
    hurdles,
    jumps,
    throws,
    columns = [],
    player,
    collegeTeam,
    nflTeam,
    position,
    isNFL,
  } = props;
  const classes = useStyles();
  const newProfileEnabled = useSelector((state: State) => Boolean(state.user?.program?.newProfileEnabled));

  const college = ((player && player.playerColleges) || [])
    .find((college:College) => college && college.isPrimary);
  const highSchool = ((player && player.highSchools) || [])
    .find((highSchool:HighSchool) => highSchool && highSchool.isPrimary);
  const graduationYear = highSchool?.graduatingClass || 0;
  const recruitingClass = graduationYear || college?.recruitingClass;

  function displayPerformance (performance:number, formatType:FormatType):string {
    if (formatType === FormatType.FEET_AND_INCHES) {
      return inchesToFeetAndInches(performance);
    } else if (formatType === FormatType.INCHES) {
        return `${Number(performance).toFixed(2)}"`;
    } else if (formatType === FormatType.MINUTES_SECONDS_MS) {
      return secondsToMinutesSecondsMs(performance);
    }

    return Number(performance).toFixed(2);
  }

  function getYearFromDate(date: Date) {
    if (!date) return 0;

    const year = new Date(date).getFullYear();
    return year;
  }

  function renderCollegeStats (name:string, stats:PlayerEventStatsComparison[]) {
    if (!stats || !stats.length) return null;
    return (
      <Accordion
        key={name}
        summary={name}
        expanded
      >
        {stats.map((stat:PlayerEventStatsComparison, index) => (
          <Ranks
            key={index}
            type={RanksType.PAI}
            playerId={player?.id}
            teamId={collegeTeam?.id}
            collegeTeamId={collegeTeam?.id}
            nflTeamId={nflTeam?.id}
            position={position}
            comparisonEvent={stat.event}
            event={{
              type: getEventName(stat.event),
              name: stat.meet
                // ? `${stat.meet} ${stat.year ? `(${stat.year})` : (!!stat.date ? `(${formatDate(stat.date) || ''})` : '')}`
                // : (stat.year ? String(stat.year) : (!!stat.date && `(${formatDate(stat.date) || ''})`)),
                ? `${stat.meet} ${!!stat.date ? `(${formatDate(stat.date)})` : stat.year ? `(${stat.year})` : ''}`
                : (stat.date ? formatDate(stat.date) : stat.year ? String(stat.year) : ''),
              result: displayPerformance(stat.performance, getEventFormatType(stat.event)),
              isCombineEvent: stat.date ? !!stat.date && (getYearFromDate(stat.date) <= (graduationYear - 3)) : !!stat.year && (stat.year <= (graduationYear - 3)),
            }}
            isNFL={isNFL}
            values={selectedColumns.map((column:Column, columnIndex:number) => {
              const comparisonStats = get(stat, 'comparisonStats', []);
              return getProfileStatsComparisonRankValue({
                column,
                columnIndex,
                comparisonStats,
                isNFL,
                newProfileEnabled,
              });
            })}
            bottomSpace
            noTopBorder
          />
          ))}
      </Accordion>
    );
  }

  function renderNFLStats (name:string, stats: PlayerEventStatsComparison[]) {
    if (!stats || !stats.length) return null;

    return (
      <Accordion
        key={name}
        summary={name}
        expanded
      >
        {stats.map((stat:PlayerEventStatsComparison, index) => (
          <Ranks
            key={index}
            type={RanksType.PAI}
            playerId={player && player.id}
            teamId={nflTeam && nflTeam.id}
            collegeTeamId={collegeTeam && collegeTeam.id}
            nflTeamId={nflTeam && nflTeam.id}
            position={position}
            comparisonEvent={stat.event}
            event={{
              type: getEventName(stat.event),
              name: stat.meet
              // ? `${stat.meet} ${stat.year ? `(${stat.year})` : ''}`
              //   : (stat.year ? String(stat.year) : ''),
                ? `${stat.meet} ${!!stat.date ? `(${formatDate(stat.date)})` : stat.year ? `(${stat.year})` : ''}`
                : (stat.date ? formatDate(stat.date) : stat.year ? String(stat.year) : ''),
              result: displayPerformance(stat.performance, getEventFormatType(stat.event)),
              isCombineEvent: !!stat.year && stat.year === graduationYear - 3,
            }}
            isNFL={isNFL}
            values={selectedColumns.map((column:Column, columnIndex:number) => {
              const comparisonStats = get(stat, 'comparisonStats', []);
              return getProfileStatsComparisonRankValue({
                column,
                columnIndex,
                comparisonStats,
                isNFL,
                newProfileEnabled,
              });
            })}
            bottomSpace
            noTopBorder
          />
          ))}
      </Accordion>
    );
  }

  const selectedColumns = (columns.filter((column:Column) => column.selected).length
    ? columns.filter((column:Column) => column.selected)
    : DEFAULT_PROFILE_STATS_COLUMNS) as Column[];

  return (
    <NamedBox
      className={classes.playerTrackAndFieldData}
      name={`Track & Field Data - ${position}`}
    >
      <Loader inProgress={loading} />

      {!isNFL && (
        <Ranks
          headerItems={selectedColumns.map((column:Column) => getCollegeHeaderItemByColumn(column, {
            recruitingClass,
            collegeTeam,
          }))}
        />
      )}
      {isNFL && (
        <Ranks
          headerItems={selectedColumns.map((column:Column) => getNflHeaderItemByColumn(column, {
            collegeTeam,
            nflTeam,
          }))}
        />
      )}

      {isNFL
        ? [
          renderNFLStats('Sprints', sprints as PlayerEventStatsComparison[]),
          renderNFLStats('Relays', relays as PlayerEventStatsComparison[]),
          renderNFLStats('Hurdles', hurdles as PlayerEventStatsComparison[]),
          renderNFLStats('Jumps', jumps as PlayerEventStatsComparison[]),
          renderNFLStats('Throws', throws as PlayerEventStatsComparison[]),
        ]
      : [
          renderCollegeStats('Sprints', sprints as PlayerEventStatsComparison[]),
          renderCollegeStats('Relays', relays as PlayerEventStatsComparison[]),
          renderCollegeStats('Hurdles', hurdles as PlayerEventStatsComparison[]),
          renderCollegeStats('Jumps', jumps as PlayerEventStatsComparison[]),
          renderCollegeStats('Throws', throws as PlayerEventStatsComparison[]),
        ]
      }
    </NamedBox>
  );
}
